import type { ComponentProps } from 'react';

import { AzureConnectTenantWizard } from '@/app/(account)/global-management/azure-tenants/azureConnectTenantWizard';
import { AwsConnectAccountWizard } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/aws/awsConnectAccountWizard';
import { AzureOnboardingWizard } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/azure/azureOnboardingWizard';
import { AzureConnectRestoreWizard } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/azure/bring-your-own/azureConnectRestoreWizard';
import { AzureConnectSourceWizard } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/azure/bring-your-own/azureConnectSourceWizard';
import { AzureOwnCloudOnboardingWizard } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/azure/bring-your-own/azureOwnCloudOnboardingWizard';
import { GcpConnectAccountWizard } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/gcp/gcpConnectAccountWizard';
import { InventoryInstancePanel } from '@/app/(dashboard)/inventory/inventoryInstancePanel';
import { BackupJobsCard } from '@/app/(dashboard)/jobs/[tab]/backupJobsCard';
import { RestoreJobsCard } from '@/app/(dashboard)/jobs/[tab]/restoreJobsCard';
import { ScanJobsCard } from '@/app/(dashboard)/jobs/[tab]/scanJobsCard';
import { AddCatalogWizardPanel } from '@/app/(dashboard)/lakehouse/catalog/wizard/AddCatalogWizardPanel';
import { ConnectorPanel } from '@/app/(dashboard)/lakehouse/connectors/connectorPanel';
import { AddConnectorWizardPanel } from '@/app/(dashboard)/lakehouse/connectors/wizard/AddConnectorWizardPanel';
import { IngestionPipelinePanel } from '@/app/(dashboard)/lakehouse/ingestion-pipelines/ingestionPipelinePanel';
import { IngestionPipelineWizardPanel } from '@/app/(dashboard)/lakehouse/ingestion-pipelines/wizard/IngestionPipelineWizardPanel';
import { UpdateIngestionPipelineWizardPanel } from '@/app/(dashboard)/lakehouse/ingestion-pipelines/wizard/UpdateIngestionPipelineWizardPanel';
import { JobTemplatePanel } from '@/app/(dashboard)/lakehouse/job-templates/jobTemplatePanel';
import { CreateJobTemplateWizardPanel } from '@/app/(dashboard)/lakehouse/job-templates/wizard/CreateJobTemplateWizardPanel';
import { UpdateJobTemplateWizardPanel } from '@/app/(dashboard)/lakehouse/job-templates/wizard/UpdateJobTemplateWizardPanel';
import { NotebookPanel } from '@/app/(dashboard)/lakehouse/notebooks/notebookPanel';
import { SqlQueryAnalyzerPanel } from '@/app/(dashboard)/lakehouse/query-editor/sqlQueryAnalyzerPanel';
import { QueryExecutionPanel } from '@/app/(dashboard)/lakehouse/query-executions/queryExecutionPanel';
import { SavedQueryPanel } from '@/app/(dashboard)/lakehouse/saved-queries/savedQueryPanel';
import { AddPrivateLinkWizardPanel } from '@/app/(dashboard)/lakehouse/settings/wizard/private-link/AddPrivateLinkWizardPanel';
import { SetupVpcPeeringWizardPanel } from '@/app/(dashboard)/lakehouse/settings/wizard/vpc-peering/SetupVpcPeeringWizardPanel';
import { SparkEnginePanel } from '@/app/(dashboard)/lakehouse/spark-engines/sparkEnginePanel';
import { SubmitJobWizardPanel } from '@/app/(dashboard)/lakehouse/spark-engines/submit-job/SubmitJobWizardPanel';
import { CreateSparkEngineWizardPanel } from '@/app/(dashboard)/lakehouse/spark-engines/wizard/CreateSparkEngineWizardPanel';
import { SparkJobPanel } from '@/app/(dashboard)/lakehouse/spark-jobs/sparkJobPanel';
import { SqlEnginePanel } from '@/app/(dashboard)/lakehouse/sql-engines/sqlEnginePanel';
import { CreateSqlEngineWizardPanel } from '@/app/(dashboard)/lakehouse/sql-engines/wizard/CreateSqlEngineWizardPanel';
import { ZeroEtlPolicySidePanel } from '@/app/(dashboard)/lakehouse/zero-etl/ZeroEtlPolicySidePanel';
import { BackupPolicyPanel } from '@/app/(dashboard)/settings/backup-policies/BackupPolicySidePanel';
import { BackupScopeResourcesPanel } from '@/app/(dashboard)/settings/backup-policies/backup-scopes/[tab]/components/backupScopeResourcesEditor';
import { ControlWizard } from '@/app/(dashboard)/settings/controls/ControlWizard';
import { ObjectFiltersPanel } from '@/app/(dashboard)/settings/object-filters/objectFiltersPanel';
import { RansomwareByConditionPanel } from '@/app/(dashboard)/settings/ransomware-detection/ransomwareByConditionPanel';
import { MongoAtlasConnectRestoreWizard } from '@/app/(dashboard)/settings/restore-accounts/mongo-atlas-projects/mongoAtlasConnectRestoreWizard';
import { RestoreMongoAtlasPrivateLinkWizard } from '@/app/(dashboard)/settings/restore-accounts/mongo-atlas-projects/private-links/restoreMongoAtlasPrivateLinkWizard';
import { ConnectSaasWorkspaceWizard } from '@/app/(dashboard)/settings/source-accounts/google-workspace/connectSaasWorkspaceWizard';
import { MongoAtlasConnectSourceWizard } from '@/app/(dashboard)/settings/source-accounts/mongo-atlas-projects/mongoAtlasConnectSourceWizard';
import { MongoAtlasPrivateLinkWizard } from '@/app/(dashboard)/settings/source-accounts/mongo-atlas-projects/private-links/mongoAtlasPrivateLinkWizard';
import { ZeroEtlPolicySidePanel as BackupZeroEtlPolicySidePanel } from '@/app/(dashboard)/settings/zero-etl-policies/ZeroEtlPolicySidePanel';
import { UpdateAccountAWSPanel } from '@/components/accounts/updateAccountAWSPanel';
import { UpdateAccountAzurePanel } from '@/components/accounts/updateAccountAzurePanel';
import { CreateGraphWizard } from '@/components/lakehouse/dashboards/createGraphWizard';

import { ConvertAllToEonSnapshotsPanel } from './ConvertAllToEonSnapshotsPanel';
import { ConvertToSingleEonSnapshotPanel } from './ConvertToSingleEonSnapshotPanel';
import { RdsCdcWizardPanel } from './instance/tabs/overview-tab/rds-cdc-wizard/RdsCdcWizardPanel';
import type { Panels } from './panels';

import { MetricsPanel } from '../accounts/job-metrics/metricsPanel';
import { RecreateAccountRoleAWSPanel } from '../accounts/recreateAccountRoleAWSPanel';
import { RecreateAccountRoleGcpPanel } from '../accounts/recreateAccountRoleGcpPanel';
import { RestoreServerConnectivityPanel } from '../accounts/restore/restore-server-connectivity/restoreServerConnectivityPanel';
import { RestoreServerTagsPanel } from '../accounts/restore/restore-server-tags/restoreServerTagsPanel';
import { UpdateAccountGCPPanel } from '../accounts/updateAccountGCPPanel';
import { ConnectAwsVaultAccountPanel } from '../accounts/vault/connectAwsVaultAccountPanel';
import { ApiCredentialsPanel } from '../apiCredentials/apiCredentialsPanel';
import { RotateApiCredentialsPanel } from '../apiCredentials/rotateApiCredentialsPanel';
import { FiltersPanel } from '../filters/filtersPanel';
import { UpsertGroupMappingPanel } from '../groups/upsertGroupMappingPanel';
import { IntegrationWizard } from '../integrations/wizard/integrationWizard';
import { ConfigureProjectLimitsWizard } from '../limits/configureProjectLimitsWizard';
import { LogSpaceRecordCard } from '../log-space/logSpaceRecordCard';
import { UpsertNotificationPolicyPanel } from '../notifications/upsertNotificationPolicyPanel';
import { CustomRolePanel } from '../permissions/customRolePanel';
import { ConvertToAMIWizard } from '../restore/convertToAMIWizard';
import { RestoreAzureDiskWizard } from '../restore/restoreAzureDiskWizard';
import { RestoreAzurePostgresWizard } from '../restore/restoreAzurePostgresWizard';
import { RestoreAzureSqlDBWizard } from '../restore/restoreAzureSqlDbWizard';
import { RestoreAzureVMPitrWizard } from '../restore/restoreAzureVMPitrWizard';
import { RestoreAzureVMWizard } from '../restore/restoreAzureVMWizard';
import { RestoreBigQueryWizard } from '../restore/restoreBigQueryWizard';
import { RestoreDocumentdbWizard } from '../restore/restoreDocumentdbWizard';
import { RestoreDynamoWizard } from '../restore/restoreDynamoWizard';
import { RestoreEc2InstanceWizard } from '../restore/restoreEc2InstanceWizard';
import { RestoreEFSWizard } from '../restore/restoreEfsWizard';
import { RestoreEksNamespaceWizard } from '../restore/restoreEksNamespaceWizard';
import { RestoreFileWizard } from '../restore/restoreFileWizard';
import { RestoreFSxWizard } from '../restore/restoreFsxWizard';
import { RestoreGcpCloudSqlWizard } from '../restore/restoreGcpCloudSqlWizard';
import { RestoreGcpDiskWizard } from '../restore/restoreGcpDiskWizard';
import { RestoreGcpVMWizard } from '../restore/restoreGcpVMWizard';
import { RestoreGoogleWorkspaceWizard } from '../restore/restoreGoogleWorkspaceWizard';
import { RestoreKeyspacesWizard } from '../restore/restoreKeyspacesWizard';
import { RestoreMongoAtlasWizard } from '../restore/restoreMongoAtlasWizard';
import { RestoreNeptuneWizard } from '../restore/restoreNeptuneWizard';
import { RestoreRDSPitrWizard } from '../restore/restoreRDSPitrWizard';
import { RestoreRDSWizard } from '../restore/restoreRDSWizard';
import { RestoreRedshiftWizard } from '../restore/restoreRedshiftWizard';
import { RestoreS3Wizard } from '../restore/restoreS3Wizard';
import { RestoreVolumeWizard } from '../restore/restoreVolumeWizard';
import { SamlPanel } from '../saml/samlPanel';
import { StorageGatewayWizard } from '../storage-gateway/wizard/storageGatewayWizard';
import { UpsertUserPanel } from '../users/upsertUserPanel';
import { CreateBackupVaultPanel } from '../vaults/createBackupVaultPanel';

type PanelsMap = {
  [k in Panels]: (
    props: Extract<PanelTypes, { panel: k }>['props']
  ) => React.ReactNode;
};

export const PanelsMap: PanelsMap = {
  InventoryInstance: InventoryInstancePanel,
  RestoreVolumeWizard: RestoreVolumeWizard,
  RestoreEc2InstanceWizard: RestoreEc2InstanceWizard,
  RestoreFileWizard: RestoreFileWizard,
  RestoreRDSWizard: RestoreRDSWizard,
  RestoreRDSPitrWizard: RestoreRDSPitrWizard,
  RestoreAzureVMPitrWizard: RestoreAzureVMPitrWizard,
  RestoreDynamoWizard: RestoreDynamoWizard,
  RestoreKeyspacesWizard: RestoreKeyspacesWizard,
  RestoreS3Wizard: RestoreS3Wizard,
  ConvertToAMIWizard: ConvertToAMIWizard,
  RestoreMongoAtlasWizard: RestoreMongoAtlasWizard,
  RestoreAzureVMWizard: RestoreAzureVMWizard,
  RestoreAzureDiskWizard: RestoreAzureDiskWizard,
  RestoreAzureSqlDBWizard: RestoreAzureSqlDBWizard,
  RestoreAzurePostgresWizard: RestoreAzurePostgresWizard,
  CreateBackupVault: CreateBackupVaultPanel,
  BackupPolicyConfig: BackupPolicyPanel,
  CustomRole: CustomRolePanel,
  ApiCredentials: ApiCredentialsPanel,
  RotateApiCredentials: RotateApiCredentialsPanel,
  Control: ControlWizard,
  NotificationPolicy: UpsertNotificationPolicyPanel,
  SAML: SamlPanel,
  Filters: FiltersPanel,
  Integration: IntegrationWizard,
  RecreateAccountRoleAWSPanel: RecreateAccountRoleAWSPanel,
  RecreateAccountRoleGCPPanel: RecreateAccountRoleGcpPanel,
  ConnectAwsVaultAccount: ConnectAwsVaultAccountPanel,
  UpsertUser: UpsertUserPanel,
  UpsertGroupMapping: UpsertGroupMappingPanel,
  RestoreServerConnectivityPanel: RestoreServerConnectivityPanel,
  RestoreServerTagsPanel: RestoreServerTagsPanel,
  MetricsPanel: MetricsPanel,
  ConvertToMultipleEonSnapshots: ConvertAllToEonSnapshotsPanel,
  ConvertToSingleEonSnapshot: ConvertToSingleEonSnapshotPanel,
  RestoreEksNamespaceWizard: RestoreEksNamespaceWizard,
  ObjectStoreBackupConfiguration: ObjectFiltersPanel,
  RestoreGcpVMWizard: RestoreGcpVMWizard,
  RestoreGcpDiskWizard: RestoreGcpDiskWizard,
  RestoreGcpCloudSqlWizard: RestoreGcpCloudSqlWizard,
  RestoreBigQueryWizard: RestoreBigQueryWizard,
  RestoreFSxWizard: RestoreFSxWizard,
  RestoreEFSWizard: RestoreEFSWizard,
  RestoreRedshiftWizard: RestoreRedshiftWizard,
  RestoreDocumentdbWizard: RestoreDocumentdbWizard,
  RestoreNeptuneWizard: RestoreNeptuneWizard,
  UpdateAccountAWSPanel: UpdateAccountAWSPanel,
  UpdateAccountGCPPanel: UpdateAccountGCPPanel,
  UpdateAccountAzurePanel: UpdateAccountAzurePanel,
  RansomwareByConditionPanel: RansomwareByConditionPanel,
  BackupScopeResourcesPanel: BackupScopeResourcesPanel,
  BackupJobsCard: BackupJobsCard,
  ScanJobsCard: ScanJobsCard,
  RestoreJobsCard: RestoreJobsCard,
  AzureOwnCloudOnboardingWizard: AzureOwnCloudOnboardingWizard,
  AzureOnboardingWizard: AzureOnboardingWizard,
  AzureConnectRestoreWizard: AzureConnectRestoreWizard,
  AzureConnectSourceWizard: AzureConnectSourceWizard,
  AzureConnectTenantWizard: AzureConnectTenantWizard,
  AwsConnectAccountWizard: AwsConnectAccountWizard,
  StorageGatewayWizard: StorageGatewayWizard,
  ConfigureProjectLimitsWizard: ConfigureProjectLimitsWizard,
  LogSpaceRecordCard: LogSpaceRecordCard,
  GcpConnectAccountWizard: GcpConnectAccountWizard,
  MongoAtlasConnectSourceWizard: MongoAtlasConnectSourceWizard,
  MongoAtlasConnectRestoreWizard: MongoAtlasConnectRestoreWizard,
  MongoAtlasPrivateLinkWizard: MongoAtlasPrivateLinkWizard,
  RestoreMongoAtlasPrivateLinkWizard: RestoreMongoAtlasPrivateLinkWizard,
  SparkEngineInstance: SparkEnginePanel,
  SqlEngineInstance: SqlEnginePanel,
  JobTemplateInstance: JobTemplatePanel,
  SavedQueryInstance: SavedQueryPanel,
  IngestionPipelineInstance: IngestionPipelinePanel,
  NotebookInstance: NotebookPanel,
  QueryExecutionInstance: QueryExecutionPanel,
  SparkJobInstance: SparkJobPanel,
  CreateGraphWizard: CreateGraphWizard,
  SqlQueryAnalyzer: SqlQueryAnalyzerPanel,
  CreateIngestionPipelineWizard: IngestionPipelineWizardPanel,
  UpdateIngestionPipelineWizard: UpdateIngestionPipelineWizardPanel,
  ConnectorInstance: ConnectorPanel,
  AddConnectorWizard: AddConnectorWizardPanel,
  AddCatalogWizard: AddCatalogWizardPanel,
  CreateSqlEngineWizard: CreateSqlEngineWizardPanel,
  CreateSparkEngineWizard: CreateSparkEngineWizardPanel,
  CreateJobTemplateWizard: CreateJobTemplateWizardPanel,
  UpdateJobTemplateWizard: UpdateJobTemplateWizardPanel,
  SubmitSparkJobWizard: SubmitJobWizardPanel,
  SetupVpcPeeringWizard: SetupVpcPeeringWizardPanel,
  AddPrivateLinkWizard: AddPrivateLinkWizardPanel,
  ZeroEtlPolicyConfig: ZeroEtlPolicySidePanel,
  BackupZeroEtlPolicyConfig: BackupZeroEtlPolicySidePanel,
  RdsCdcWizard: RdsCdcWizardPanel,
  RestoreGoogleWorkspaceWizard: RestoreGoogleWorkspaceWizard,
  ConnectSaasWorkspaceWizard: ConnectSaasWorkspaceWizard,
};

export type PanelTypes =
  | {
      panel: typeof Panels.InventoryInstance;
      props: ComponentProps<typeof InventoryInstancePanel>;
    }
  | {
      panel: typeof Panels.CreateBackupVault;
      props: ComponentProps<typeof CreateBackupVaultPanel>;
    }
  | {
      panel: typeof Panels.RestoreVolumeWizard;
      props: ComponentProps<typeof RestoreVolumeWizard>;
    }
  | {
      panel: typeof Panels.RestoreEc2InstanceWizard;
      props: ComponentProps<typeof RestoreEc2InstanceWizard>;
    }
  | {
      panel: typeof Panels.RestoreFileWizard;
      props: ComponentProps<typeof RestoreFileWizard>;
    }
  | {
      panel: typeof Panels.RestoreRDSWizard;
      props: ComponentProps<typeof RestoreRDSWizard>;
    }
  | {
      panel: typeof Panels.RestoreRDSPitrWizard;
      props: ComponentProps<typeof RestoreRDSPitrWizard>;
    }
  | {
      panel: typeof Panels.RestoreDynamoWizard;
      props: ComponentProps<typeof RestoreDynamoWizard>;
    }
  | {
      panel: typeof Panels.RestoreKeyspacesWizard;
      props: ComponentProps<typeof RestoreKeyspacesWizard>;
    }
  | {
      panel: typeof Panels.RestoreS3Wizard;
      props: ComponentProps<typeof RestoreS3Wizard>;
    }
  | {
      panel: typeof Panels.RestoreMongoAtlasWizard;
      props: ComponentProps<typeof RestoreMongoAtlasWizard>;
    }
  | {
      panel: typeof Panels.RestoreAzureVMWizard;
      props: ComponentProps<typeof RestoreAzureVMWizard>;
    }
  | {
      panel: typeof Panels.RestoreAzureVMPitrWizard;
      props: ComponentProps<typeof RestoreAzureVMPitrWizard>;
    }
  | {
      panel: typeof Panels.RestoreAzureDiskWizard;
      props: ComponentProps<typeof RestoreAzureDiskWizard>;
    }
  | {
      panel: typeof Panels.RestoreAzureSqlDBWizard;
      props: ComponentProps<typeof RestoreAzureSqlDBWizard>;
    }
  | {
      panel: typeof Panels.RestoreAzurePostgresWizard;
      props: ComponentProps<typeof RestoreAzurePostgresWizard>;
    }
  | {
      panel: typeof Panels.ConvertToAMIWizard;
      props: ComponentProps<typeof ConvertToAMIWizard>;
    }
  | {
      panel: typeof Panels.NotificationPolicy;
      props: ComponentProps<typeof UpsertNotificationPolicyPanel>;
    }
  | {
      panel: typeof Panels.SAML;
      props: ComponentProps<typeof SamlPanel>;
    }
  | {
      panel: typeof Panels.Filters;
      props: ComponentProps<typeof FiltersPanel>;
    }
  | {
      panel: typeof Panels.BackupPolicyConfig;
      props: ComponentProps<typeof BackupPolicyPanel>;
    }
  | {
      panel: typeof Panels.CustomRole;
      props: ComponentProps<typeof CustomRolePanel>;
    }
  | {
      panel: typeof Panels.ApiCredentials;
      props: ComponentProps<typeof ApiCredentialsPanel>;
    }
  | {
      panel: typeof Panels.RotateApiCredentials;
      props: ComponentProps<typeof RotateApiCredentialsPanel>;
    }
  | {
      panel: typeof Panels.Control;
      props: ComponentProps<typeof ControlWizard>;
    }
  | {
      panel: typeof Panels.RecreateAccountRoleAWSPanel;
      props: ComponentProps<typeof RecreateAccountRoleAWSPanel>;
    }
  | {
      panel: typeof Panels.RecreateAccountRoleGCPPanel;
      props: ComponentProps<typeof RecreateAccountRoleGcpPanel>;
    }
  | {
      panel: typeof Panels.ConnectAwsVaultAccount;
      props: ComponentProps<typeof ConnectAwsVaultAccountPanel>;
    }
  | {
      panel: typeof Panels.UpdateAccountAWSPanel;
      props: ComponentProps<typeof UpdateAccountAWSPanel>;
    }
  | {
      panel: typeof Panels.UpdateAccountGCPPanel;
      props: ComponentProps<typeof UpdateAccountGCPPanel>;
    }
  | {
      panel: typeof Panels.UpdateAccountAzurePanel;
      props: ComponentProps<typeof UpdateAccountAzurePanel>;
    }
  | {
      panel: typeof Panels.UpsertUser;
      props: ComponentProps<typeof UpsertUserPanel>;
    }
  | {
      panel: typeof Panels.UpsertGroupMapping;
      props: ComponentProps<typeof UpsertGroupMappingPanel>;
    }
  | {
      panel: typeof Panels.RestoreServerConnectivityPanel;
      props: ComponentProps<typeof RestoreServerConnectivityPanel>;
    }
  | {
      panel: typeof Panels.RestoreServerTagsPanel;
      props: ComponentProps<typeof RestoreServerTagsPanel>;
    }
  | {
      panel: typeof Panels.MetricsPanel;
      props: ComponentProps<typeof MetricsPanel>;
    }
  | {
      panel: typeof Panels.ConvertToMultipleEonSnapshots;
      props: ComponentProps<typeof ConvertAllToEonSnapshotsPanel>;
    }
  | {
      panel: typeof Panels.ConvertToSingleEonSnapshot;
      props: ComponentProps<typeof ConvertToSingleEonSnapshotPanel>;
    }
  | {
      panel: typeof Panels.RestoreEksNamespaceWizard;
      props: ComponentProps<typeof RestoreEksNamespaceWizard>;
    }
  | {
      panel: typeof Panels.ObjectStoreBackupConfiguration;
      props: ComponentProps<typeof ObjectFiltersPanel>;
    }
  | {
      panel: typeof Panels.RestoreGcpVMWizard;
      props: ComponentProps<typeof RestoreGcpVMWizard>;
    }
  | {
      panel: typeof Panels.RestoreGcpDiskWizard;
      props: ComponentProps<typeof RestoreGcpDiskWizard>;
    }
  | {
      panel: typeof Panels.RestoreGcpCloudSqlWizard;
      props: ComponentProps<typeof RestoreGcpCloudSqlWizard>;
    }
  | {
      panel: typeof Panels.RestoreBigQueryWizard;
      props: ComponentProps<typeof RestoreBigQueryWizard>;
    }
  | {
      panel: typeof Panels.RestoreFSxWizard;
      props: ComponentProps<typeof RestoreFSxWizard>;
    }
  | {
      panel: typeof Panels.RestoreEFSWizard;
      props: ComponentProps<typeof RestoreEFSWizard>;
    }
  | {
      panel: typeof Panels.RestoreRedshiftWizard;
      props: ComponentProps<typeof RestoreRedshiftWizard>;
    }
  | {
      panel: typeof Panels.RestoreDocumentdbWizard;
      props: ComponentProps<typeof RestoreDocumentdbWizard>;
    }
  | {
      panel: typeof Panels.RestoreNeptuneWizard;
      props: ComponentProps<typeof RestoreNeptuneWizard>;
    }
  | {
      panel: typeof Panels.RansomwareConditionPanel;
      props: ComponentProps<typeof RansomwareByConditionPanel>;
    }
  | {
      panel: typeof Panels.BackupScopeResourcesPanel;
      props: ComponentProps<typeof BackupScopeResourcesPanel>;
    }
  | {
      panel: typeof Panels.ScanJobsCard;
      props: ComponentProps<typeof ScanJobsCard>;
    }
  | {
      panel: typeof Panels.BackupJobsCard;
      props: ComponentProps<typeof BackupJobsCard>;
    }
  | {
      panel: typeof Panels.RestoreJobsCard;
      props: ComponentProps<typeof RestoreJobsCard>;
    }
  | {
      panel: typeof Panels.AzureOwnCloudOnboardingWizard;
      props: ComponentProps<typeof AzureOwnCloudOnboardingWizard>;
    }
  | {
      panel: typeof Panels.AzureOnboardingWizard;
      props: ComponentProps<typeof AzureOnboardingWizard>;
    }
  | {
      panel: typeof Panels.AzureConnectRestoreWizard;
      props: ComponentProps<typeof AzureConnectRestoreWizard>;
    }
  | {
      panel: typeof Panels.AzureConnectSourceWizard;
      props: ComponentProps<typeof AzureConnectSourceWizard>;
    }
  | {
      panel: typeof Panels.AwsConnectAccountWizard;
      props: ComponentProps<typeof AwsConnectAccountWizard>;
    }
  | {
      panel: typeof Panels.GcpConnectAccountWizard;
      props: ComponentProps<typeof GcpConnectAccountWizard>;
    }
  | {
      panel: typeof Panels.MongoAtlasConnectSourceWizard;
      props: ComponentProps<typeof MongoAtlasConnectSourceWizard>;
    }
  | {
      panel: typeof Panels.MongoAtlasConnectRestoreWizard;
      props: ComponentProps<typeof MongoAtlasConnectRestoreWizard>;
    }
  | {
      panel: typeof Panels.MongoAtlasPrivateLinkWizard;
      props: ComponentProps<typeof MongoAtlasPrivateLinkWizard>;
    }
  | {
      panel: typeof Panels.RestoreMongoAtlasPrivateLinkWizard;
      props: ComponentProps<typeof RestoreMongoAtlasPrivateLinkWizard>;
    }
  | {
      panel: typeof Panels.AzureConnectTenantWizard;
      props: ComponentProps<typeof AzureConnectTenantWizard>;
    }
  | {
      panel: typeof Panels.StorageGatewayWizard;
      props: ComponentProps<typeof StorageGatewayWizard>;
    }
  | {
      panel: typeof Panels.ConfigureProjectLimitsWizard;
      props: ComponentProps<typeof ConfigureProjectLimitsWizard>;
    }
  | {
      panel: typeof Panels.Integration;
      props: ComponentProps<typeof IntegrationWizard>;
    }
  | {
      panel: typeof Panels.LogSpaceRecordCard;
      props: ComponentProps<typeof LogSpaceRecordCard>;
    }
  | {
      panel: typeof Panels.SparkEngineInstance;
      props: ComponentProps<typeof SparkEnginePanel>;
    }
  | {
      panel: typeof Panels.SqlEngineInstance;
      props: ComponentProps<typeof SqlEnginePanel>;
    }
  | {
      panel: typeof Panels.JobTemplateInstance;
      props: ComponentProps<typeof JobTemplatePanel>;
    }
  | {
      panel: typeof Panels.SavedQueryInstance;
      props: ComponentProps<typeof SavedQueryPanel>;
    }
  | {
      panel: typeof Panels.IngestionPipelineInstance;
      props: ComponentProps<typeof IngestionPipelinePanel>;
    }
  | {
      panel: typeof Panels.NotebookInstance;
      props: ComponentProps<typeof NotebookPanel>;
    }
  | {
      panel: typeof Panels.QueryExecutionInstance;
      props: ComponentProps<typeof QueryExecutionPanel>;
    }
  | {
      panel: typeof Panels.SparkJobInstance;
      props: ComponentProps<typeof SparkJobPanel>;
    }
  | {
      panel: typeof Panels.CreateGraphWizard;
      props: ComponentProps<typeof CreateGraphWizard>;
    }
  | {
      panel: typeof Panels.SqlQueryAnalyzer;
      props: ComponentProps<typeof SqlQueryAnalyzerPanel>;
    }
  | {
      panel: typeof Panels.CreateIngestionPipelineWizard;
      props: ComponentProps<typeof IngestionPipelineWizardPanel>;
    }
  | {
      panel: typeof Panels.UpdateIngestionPipelineWizard;
      props: ComponentProps<typeof UpdateIngestionPipelineWizardPanel>;
    }
  | {
      panel: typeof Panels.ConnectorInstance;
      props: ComponentProps<typeof ConnectorPanel>;
    }
  | {
      panel: typeof Panels.AddConnectorWizard;
      props: ComponentProps<typeof AddConnectorWizardPanel>;
    }
  | {
      panel: typeof Panels.AddCatalogWizard;
      props: ComponentProps<typeof AddCatalogWizardPanel>;
    }
  | {
      panel: typeof Panels.CreateSqlEngineWizard;
      props: ComponentProps<typeof CreateSqlEngineWizardPanel>;
    }
  | {
      panel: typeof Panels.CreateSparkEngineWizard;
      props: ComponentProps<typeof CreateSparkEngineWizardPanel>;
    }
  | {
      panel: typeof Panels.CreateJobTemplateWizard;
      props: ComponentProps<typeof CreateJobTemplateWizardPanel>;
    }
  | {
      panel: typeof Panels.UpdateJobTemplateWizard;
      props: ComponentProps<typeof UpdateJobTemplateWizardPanel>;
    }
  | {
      panel: typeof Panels.SubmitSparkJobWizard;
      props: ComponentProps<typeof SubmitJobWizardPanel>;
    }
  | {
      panel: typeof Panels.SetupVpcPeeringWizard;
      props: ComponentProps<typeof SetupVpcPeeringWizardPanel>;
    }
  | {
      panel: typeof Panels.AddPrivateLinkWizard;
      props: ComponentProps<typeof AddPrivateLinkWizardPanel>;
    }
  | {
      panel: typeof Panels.ZeroEtlPolicyConfig;
      props: ComponentProps<typeof ZeroEtlPolicySidePanel>;
    }
  | {
      panel: typeof Panels.BackupZeroEtlPolicyConfig;
      props: ComponentProps<typeof BackupZeroEtlPolicySidePanel>;
    }
  | {
      panel: typeof Panels.RdsCdcWizard;
      props: ComponentProps<typeof RdsCdcWizardPanel>;
    }
  | {
      panel: typeof Panels.RestoreGoogleWorkspaceWizard;
      props: ComponentProps<typeof RestoreGoogleWorkspaceWizard>;
    }
  | {
      panel: typeof Panels.ConnectSaasWorkspaceWizard;
      props: ComponentProps<typeof ConnectSaasWorkspaceWizard>;
    };
